import React, { useEffect, useRef, useState } from 'react';
import { organizerAvatarColor, organizerInitials } from '@radevents/shared';
import OrganizerVerifiedBadge from '../OrganizerVerifiedBadge';
import { BERRY, BORDER, FONT_HEAD, FONT_MONO, INK, MUTED } from './organizerSpace';

interface Props {
  name: string;
  logoUrl?: string;
  bannerUrl?: string;
  /** Ein bestätigtes Konto pflegt dieses Profil — der Haken. Kostenlos. */
  verified?: boolean;
  /** Der Betrachter pflegt diesen Veranstalter. */
  manage?: boolean;
  /**
   * Premium läuft — dann ist auch der **Banner**-Platz ein echtes Upload-Feld.
   *
   * Das Logo hängt nicht daran: es ist die Identität des Vereins, dieselbe Aussage wie der Haken,
   * und die verkaufen wir nicht.
   */
  premium?: boolean;
  uploading?: boolean;
  /**
   * Eine Datei wurde gewählt — hochgeladen wird sie erst nach der Vorschau.
   *
   * Der Zwischenschritt gehört nicht hierher, sondern zur Seite: sie kennt den Fortschritt und
   * blendet den Dialog ein (siehe AssetUploadPreview).
   */
  onPick?: (kind: 'logo' | 'banner', file: File) => void;
  onRemove?: (kind: 'logo' | 'banner') => void;
  /**
   * Ein leerer Platz, der etwas Bezahltes verspricht, wurde angetippt — öffnet den Premium-Dialog.
   *
   * Ohne Anlass-Parameter: alle Haken der Seite führen in denselben Dialog. Eine Variante je
   * Anlass hieße mehrere Erklärungen für dieselbe Sache, die auseinanderlaufen.
   */
  onPremiumHint?: () => void;
  logoUploaded?: boolean;
  bannerUploaded?: boolean;
  /**
   * Vorschau des noch nicht freigegebenen Bildes (Blob-Adresse, siehe `loadAuthedImage`).
   *
   * Sie ersetzt in der Anzeige das freigegebene Bild — sonst lädt jemand ein Logo hoch, sieht
   * weiter das alte und lädt es ein zweites Mal.
   */
  logoPendingUrl?: string | null;
  bannerPendingUrl?: string | null;
  /**
   * Eine Zeile unter dem Namen: kommende Events und Ort (30.10.2026, Vorbild Posh:
   * „79 events · 1.303 attendees"). Sagt in einem Blick, ob sich die Seite lohnt.
   */
  meta?: string;
}

const CSS = `
/* Kein Überlappen mehr.
   Vorher zog ein negativer Rand den Kopf 46px in den Banner hinein — hübsch, aber der Kopf steht
   auf position:relative mit z-index:1 und legte sich damit als unsichtbare Fläche über die untere
   Bannerkante. Genau dort sitzen „Banner ändern" und „Entfernen": sichtbar, aber nicht mehr
   anklickbar. Eine Gestaltung, die Knöpfe verschluckt, ist keine. */
.om-banner { position:relative; height:148px; border-radius:18px; overflow:hidden; margin-bottom:16px;
  background:#EDE8E8; border:1px solid ${BORDER}; }
.om-banner img { width:100%; height:100%; object-fit:cover; display:block; }
.om-banner--empty { display:flex; align-items:center; justify-content:center; border-style:dashed;
  cursor:pointer; background:repeating-linear-gradient(135deg,#F3EFEF 0 12px,#EFEAEA 12px 24px); }
.om-slot-label { font-family:${FONT_MONO}; font-size:10px; letter-spacing:.12em; text-transform:uppercase;
  color:${MUTED}; }
/* Die Aktion sitzt auf dem Gegenstand, den sie veraendert. Vorher standen „Banner aendern" und
   „Logo aendern" als freie Textknoepfe daneben — vier Knoepfe im Kopf, von denen keiner sagte,
   welches Bild er meint. */
.om-banner-actions { position:absolute; right:12px; bottom:12px; display:flex; gap:8px; }
.om-edit { display:inline-flex; align-items:center; gap:7px; font-family:inherit; font-size:12.5px;
  font-weight:700; border-radius:999px; padding:7px 13px 7px 11px; cursor:pointer;
  border:1px solid rgba(255,255,255,.5); background:rgba(44,44,44,.55); color:#fff;
  backdrop-filter:blur(3px); }
.om-edit:hover:not(:disabled) { background:rgba(44,44,44,.72); }
.om-edit:disabled { cursor:default; opacity:.6; }
/* Am Logo als runder Knopf ueber der Kreiskante — er sitzt in .om-logo-wrap, NICHT im Kreis:
   .om-logo schneidet mit overflow:hidden ab. */
.om-edit--logo { position:absolute; right:-2px; bottom:-2px; width:30px; height:30px; padding:0;
  justify-content:center; background:#fff; color:${INK}; border:1px solid ${BORDER};
  box-shadow:0 2px 6px rgba(44,44,44,.16); }
.om-edit--logo:hover:not(:disabled) { background:#FBFAFA; }
.om-edit:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; }
/* Auswahl hinter dem Stift: „ersetzen" und „entfernen" sind zwei Wege zu einem Bild, kein Grund
   fuer zwei Dauerknoepfe. */
.om-menu { position:absolute; z-index:3; min-width:172px; background:#fff; border:1px solid ${BORDER};
  border-radius:12px; padding:6px; box-shadow:0 10px 30px -12px rgba(44,44,44,.45); }
.om-menu--banner { right:12px; bottom:52px; }
.om-menu--logo { left:0; top:calc(100% + 8px); }
.om-menu button { display:block; width:100%; text-align:left; font-family:inherit; font-size:14px;
  font-weight:600; color:${INK}; background:none; border:0; border-radius:8px; padding:9px 11px;
  cursor:pointer; }
.om-menu button:hover { background:#F5F3F3; }
.om-menu button:focus-visible { outline:2px solid ${BERRY}; outline-offset:-2px; }
/* „Wird geprüft" liegt auf dem Bild und nicht daneben: das Bild selbst ist die Aussage, an die
   der Hinweis gehört — sonst sucht man, welches der beiden gemeint ist. */
.om-pending { position:absolute; left:12px; top:12px; font-family:${FONT_MONO}; font-size:10px;
  letter-spacing:.1em; text-transform:uppercase; background:rgba(44,44,44,.82); color:#fff;
  padding:5px 9px; border-radius:7px; }
/* Der Vermerk am Logo haengt UNTER dem Kreis und darf deshalb nicht in ihm stecken: .om-logo
   schneidet mit overflow:hidden alles ab, was ueber den Rand ragt (sonst staende ein eckiges Bild
   ueber der runden Flaeche). Deshalb eine Huelle darum, die nichts abschneidet. */
.om-logo-wrap { position:relative; flex:0 0 auto; }
.om-pending--logo { left:50%; top:auto; bottom:-11px; transform:translateX(-50%); font-size:9.5px;
  padding:3px 8px; white-space:nowrap; }
/* Mittig statt auf einer Grundlinie: Name und Kachel sind zwei verschieden hohe Blöcke, und
   unten ausgerichtet hing der Text am Fuß des Logos statt neben ihm. */
.om-head { display:flex; align-items:center; gap:18px; flex-wrap:wrap; position:relative; z-index:1;
  padding-bottom:6px; }
/* Rund, wie an jeder anderen Stelle im Projekt (Veranstalterliste, Vorschlagsprüfung, App).
   Eine einzelne Seite mit abgerundetem Quadrat liest sich als Fehler, nicht als Gestaltung. */
.om-logo { width:84px; height:84px; flex:0 0 84px; border-radius:50%; background:#fff; overflow:hidden;
  border:1px solid ${BORDER}; display:flex; align-items:center; justify-content:center; }
/* Eingepasst statt beschnitten: ein Vereinswappen darf nicht angeschnitten werden. Das Polster
   hält breite Schriftzüge vom Rand der Kreisfläche weg. (Keine Backticks in diesem Block — er
   steht in einem Template-Literal und würde es beenden.) */
.om-logo img { width:100%; height:100%; object-fit:contain; padding:11px; box-sizing:border-box; }
.om-logo--empty { border-style:dashed; cursor:pointer; flex-direction:column; gap:4px; text-align:center; padding:6px; }
.om-initials { font-family:${FONT_HEAD}; font-weight:800; font-size:36px; color:#fff; width:100%; height:100%;
  display:flex; align-items:center; justify-content:center; }
.om-names { min-width:0; flex:1; }
.om-meta { margin:8px 0 0; font-size:15px; color:${MUTED}; }
/* Werkzeugleiste des Pflege-Zustands: Ansage links, Aktion rechts. Die Aktion haengt an
   margin-left:auto statt an space-between (keine Backticks hier — der Block steht in einem
   Template-Literal), damit sie beim Umbruch auf schmalen Schirmen
   unter der Ansage links steht und nicht allein am rechten Rand. */
.om-hint { display:flex; flex-wrap:wrap; align-items:center; gap:10px; margin:14px 0 2px; padding:10px 12px;
  border-radius:12px; background:rgba(146,44,87,.06); font-size:13.5px; color:${INK}; }
@media (max-width:640px) {
  .om-banner { height:118px; margin-bottom:14px; }
  .om-logo { width:68px; height:68px; flex-basis:68px; }
  .om-initials { font-size:26px; }
  .om-head { gap:14px; flex-wrap:nowrap; }
  .om-head .vb-h1 { font-size:27px; }
  .om-meta { font-size:14px; margin-top:5px; }
}
`;

/**
 * Der Kopf der Veranstalterseite: Banner, Logo, Name, Haken.
 *
 * Dieselbe Gestalt für Besucher und für den Verein selbst — der Unterschied ist, dass leere
 * Plätze im Pflege-Zustand ansprechbar sind. Sie erscheinen nur für Angaben, die der Betrachter
 * auch pflegen darf: der **Logo**-Platz für jeden Verwalter (das Logo ist seit dem 25.08.2026
 * frei), der **Banner**-Platz nur mit Premium (seit 01.09.2026, siehe `showBannerSlot`). Beide
 * sind damit eine Aufforderung und keine Preisliste.
 *
 * Besucher sehen leere Plätze **nicht**. Für sie wäre ein gestrichelter Kasten ein Fehler der
 * Seite, kein Angebot.
 */
/** Schloss statt Stift, wo die Aktion zu Premium gehört — dieselbe Marke wie an allen Haken. */
const Lock: React.FC = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.1"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="4" y="10.5" width="16" height="10" rx="2" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </svg>
);

const Pencil: React.FC = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.1"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const OrganizerMasthead: React.FC<Props> = ({
  name, logoUrl, bannerUrl, verified, manage, premium, uploading,
  onPick, onRemove, onPremiumHint, logoUploaded, bannerUploaded,
  logoPendingUrl, bannerPendingUrl, meta,
}) => {
  const logoInput = useRef<HTMLInputElement>(null);
  const bannerInput = useRef<HTMLInputElement>(null);
  /** Welches Bild gerade seine Auswahl offen hat. */
  const [menu, setMenu] = useState<'logo' | 'banner' | null>(null);

  // Draussen tippen oder Escape schliesst die Auswahl — ein Menue, das nur der eigene Knopf
  // schliesst, bleibt beim naechsten Klick woanders offen stehen.
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', onKey); };
  }, [menu]);

  const pick = (kind: 'logo' | 'banner') => {
    setMenu(null);
    // Nur der Banner ist eine Kaufentscheidung. Das Logo hier ebenfalls zu sperren hiesse, dem
    // Verein seine eigene Marke zu verkaufen — und der leere Logo-Platz waere eine Verkaufsflaeche
    // fuer etwas, das er ohnehin bekommt.
    if (kind === 'banner' && !premium) { onPremiumHint?.(); return; }
    (kind === 'logo' ? logoInput : bannerInput).current?.click();
  };

  /**
   * Stift am Bild. Gibt es eine hochgeladene Datei, fuehrt er in die Auswahl (ersetzen /
   * entfernen); sonst direkt in den Dateidialog — ein Menue mit einem Eintrag ist ein Umweg.
   */
  const editButton = (kind: 'logo' | 'banner', uploaded?: boolean) => {
    const label = kind === 'logo' ? 'Logo' : 'Banner';
    const canRemove = !!uploaded && !!onRemove;
    // Steht schon ein Bild im Banner (etwa ein von der Redaktion gepflegtes Vereinsfoto), ohne
    // dass Premium läuft, wird aus dem Stift der Haken: die Pille sagt dann, was sie kostet,
    // statt in einen Dateidialog zu führen, den der Server mit 402 beendet.
    const locked = kind === 'banner' && !premium;
    return (
      <button
        type="button"
        className={kind === 'logo' ? 'om-edit om-edit--logo' : 'om-edit'}
        disabled={uploading}
        title={locked ? 'Ein eigenes Vereinsbanner gehört zu Premium.' : undefined}
        aria-label={locked ? 'Eigenes Banner mit Premium'
          : canRemove ? `${label} ersetzen oder entfernen` : `${label} ändern`}
        aria-haspopup={!locked && canRemove ? 'menu' : undefined}
        aria-expanded={!locked && canRemove ? menu === kind : undefined}
        onClick={e => {
          e.stopPropagation();
          if (locked) { pick(kind); return; }
          if (canRemove) setMenu(menu === kind ? null : kind); else pick(kind);
        }}
      >
        {locked ? <Lock /> : <Pencil />}
        {kind === 'banner' && (locked ? 'Eigenes Banner mit Premium' : label)}
      </button>
    );
  };

  const editMenu = (kind: 'logo' | 'banner') => (
    menu === kind && (
      <div className={`om-menu om-menu--${kind}`} role="menu" onClick={e => e.stopPropagation()}>
        <button type="button" role="menuitem" onClick={() => pick(kind)}>
          {kind === 'logo' ? 'Logo ersetzen' : 'Banner ersetzen'}
        </button>
        <button type="button" role="menuitem" onClick={() => { setMenu(null); onRemove?.(kind); }}>
          {kind === 'logo' ? 'Logo entfernen' : 'Banner entfernen'}
        </button>
      </div>
    )
  );

  // In der Pflege-Ansicht gilt das wartende Bild: es ist das, was der Veranstalter gerade
  // hochgeladen hat. Besucher sehen unverändert nur das freigegebene.
  const shownLogo = logoPendingUrl || logoUrl;
  const shownBanner = bannerPendingUrl || bannerUrl;
  /**
   * Der leere Banner-Platz erscheint nur, wenn er auch benutzbar ist.
   *
   * Bis zum 01.09.2026 war er die Verkaufsfläche für Premium: ein gestrichelter Streifen mit
   * Schloss, ganz oben auf der eigenen Seite. Das Erste, was ein Basis-Verwalter beim Öffnen
   * seines Bereichs sah, war damit eine gesperrte Fläche — vor seinem Namen, seinen Stammdaten
   * und seinen Terminen. Das Dashboard ist zuerst sein Arbeitsplatz; ein Angebot, das die Arbeit
   * überdeckt, verkauft nicht, es steht im Weg. Das Angebot steht weiterhin im Pflege-Balken und
   * im Premium-Dialog.
   *
   * Ein vorhandener Banner bleibt sichtbar, auch ohne Premium: er kann von der Redaktion
   * gepflegt sein (`photo_url`), und dann gehört er auf die Seite.
   */
  const showBannerSlot = (manage && premium) || !!shownBanner;

  return (
    <div style={{ marginBottom: 26 }}>
      <style>{CSS}</style>

      {showBannerSlot && (
        shownBanner ? (
          <div className="om-banner">
            <img src={shownBanner} alt={`Banner von ${name}`} />
            {bannerPendingUrl && <span className="om-pending">Wird geprüft</span>}
            {manage && (
              <div className="om-banner-actions">{editButton('banner', bannerUploaded)}</div>
            )}
            {manage && editMenu('banner')}
          </div>
        ) : (
          <div
            className="om-banner om-banner--empty"
            role="button"
            tabIndex={0}
            onClick={() => pick('banner')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick('banner'); } }}
          >
            {/* Ohne Premium wird dieser Zweig nicht mehr gerendert (siehe showBannerSlot) — der
                leere Platz ist eine Einladung, keine Preisliste. */}
            <span className="om-slot-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              + Banner hochladen
            </span>
          </div>
        )
      )}

      <div className="om-head" style={{ paddingTop: 4 }}>
        {shownLogo ? (
          <div className="om-logo-wrap">
            <div className="om-logo">
              <img src={shownLogo} alt={name} />
            </div>
            {logoPendingUrl && <span className="om-pending om-pending--logo">Wird geprüft</span>}
            {manage && editButton('logo', logoUploaded)}
            {manage && editMenu('logo')}
          </div>
        ) : manage ? (
          <div
            className="om-logo om-logo--empty"
            role="button"
            tabIndex={0}
            onClick={() => pick('logo')}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick('logo'); } }}
          >
            <span className="om-slot-label">+ Logo</span>
          </div>
        ) : (
          <div className="om-logo" style={{ background: organizerAvatarColor(name), border: 'none' }}>
            <span className="om-initials">{organizerInitials(name)}</span>
          </div>
        )}

        <div className="om-names">
          <div className="vb-eyebrow">Veranstalter</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            <h1 className="vb-h1">{name}</h1>
            <OrganizerVerifiedBadge verified={verified} />
          </div>
          {meta && <p className="om-meta">{meta}</p>}
        </div>

      </div>

      <input ref={logoInput} data-asset="logo" type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) onPick?.('logo', f); e.target.value = ''; }} />
      <input ref={bannerInput} data-asset="banner" type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) onPick?.('banner', f); e.target.value = ''; }} />
    </div>
  );
};

export default OrganizerMasthead;
